import { createFileRoute } from '@tanstack/react-router'
import { useTranslations, useLocale } from 'use-intl'

export const Route = createFileRoute('/{-$locale}/_public/terms-of-service')({
  component: TermsOfServicePage,
})

const content = {
  en: {
    lastUpdated: 'Last Updated: May 15, 2026',
    sections: [
      {
        title: '1. Acceptance of Terms',
        body: 'By accessing and using TabeTabi (the "Service"), you accept and agree to be bound by the terms and provisions of this agreement. In addition, when using this Service, you shall be subject to any posted guidelines or rules applicable to such services.'
      },
      {
        title: '2. Description of Service',
        body: 'TabeTabi provides users with access to a rich collection of resources, including restaurant discovery tools, interactive maps, AI-driven dining recommendations, and personal collection management (favorites and private notes). You understand and agree that the Service may include certain communications from us, such as service announcements and administrative messages.'
      },
      {
        title: '3. User Accounts & Responsibilities',
        body: 'To use certain features (like saving favorites and writing notes), you must register for an account using supported OAuth providers (e.g., Google) or email credentials. You are responsible for maintaining the confidentiality of your account information. You agree to immediately notify us of any unauthorized use of your account or any other breach of security.'
      },
      {
        title: '4. Third-Party Content & Data Accuracy',
        body: 'Our platform relies on various data sources, including third-party databases and AI models, to provide restaurant information, translations, and recommendations. While we strive for accuracy, we cannot guarantee that all details (such as business hours, menus, and prices) are strictly up-to-date or accurate. We strongly recommend verifying critical information via official sources or directly with the restaurant.'
      },
      {
        title: '5. Limitation of Liability',
        body: 'TabeTabi shall not be liable for any direct, indirect, incidental, special, or consequential damages resulting from the use or inability to use the Service, including but not limited to reliance on information obtained from the Service, mistakes, omissions, interruptions, or errors.'
      },
      {
        title: '6. Modifications to Terms',
        body: 'We reserve the right to modify these terms at any time. We will post the most current version on our site. By continuing to use the service after changes become effective, you agree to be bound by the revised terms.'
      }
    ]
  },
  'zh-CN': {
    lastUpdated: '最后更新日期：2026年5月15日',
    sections: [
      {
        title: '1. 接受条款',
        body: '通过访问和使用 TabeTabi (食べ旅)（以下简称"服务"），即表示您接受并同意受本协议条款和条件的约束。此外，在使用本服务时，您应遵守适用于此类服务的任何已发布的指南或规则。'
      },
      {
        title: '2. 服务说明',
        body: 'TabeTabi 为用户提供丰富的资源，包括餐厅发现工具、交互式地图、AI 驱动的就餐推荐，以及个人收藏管理（收藏夹和私人笔记）。您理解并同意，本服务可能会包含来自我们的某些通信，例如服务公告和管理消息。'
      },
      {
        title: '3. 用户账户与责任',
        body: '为了使用某些功能（如保存收藏和撰写笔记），您必须使用我们支持的 OAuth 提供商（例如 Google）或邮箱注册账户。您有责任对您的账户信息保密。您同意在发现任何未经授权使用您的账户或其他安全漏洞时立即通知我们。'
      },
      {
        title: '4. 第三方内容与数据准确性',
        body: '我们的平台依赖多种数据源（包括第三方数据库和 AI 模型）来提供餐厅信息、翻译和推荐。尽管我们努力确保准确性，但我们无法保证所有细节（如营业时间、菜单和价格）绝对最新或完全准确。我们强烈建议您通过官方渠道或直接与餐厅核实关键信息。'
      },
      {
        title: '5. 责任限制',
        body: '在法律允许的最大范围内，TabeTabi 对因使用或无法使用本服务（包括但不限于依赖从本服务获得的信息、错误、遗漏、中断或瑕疵）而导致的任何直接、间接、偶然、特殊或后果性的损害不承担责任。'
      },
      {
        title: '6. 条款修改',
        body: '我们保留随时修改本服务条款的权利。我们将始终在网站上发布最新版本。在变更生效后继续使用本服务，即表示您同意受修订后的条款约束。'
      }
    ]
  },
  'zh-TW': {
    lastUpdated: '最後更新日期：2026年5月15日',
    sections: [
      {
        title: '1. 接受條款',
        body: '透過造訪和使用 TabeTabi (食べ旅)（以下簡稱"服務"），即表示您接受並同意受本協議條款和條件的約束。此外，在使用本服務時，您應遵守適用於此類服務的任何已發佈的指南或規則。'
      },
      {
        title: '2. 服務說明',
        body: 'TabeTabi 為使用者提供豐富的資源，包括餐廳發現工具、互動式地圖、AI 驅動的用餐推薦，以及個人收藏管理（收藏夾和私人筆記）。您理解並同意，本服務可能會包含來自我們的某些通訊，例如服務公告和管理訊息。'
      },
      {
        title: '3. 使用者帳戶與責任',
        body: '為了使用某些功能（如儲存收藏和撰寫筆記），您必須使用我們支援的 OAuth 提供商（例如 Google）或信箱註冊帳戶。您有責任對您的帳戶資訊保密。您同意在發現任何未經授權使用您的帳戶或其他安全漏洞時立即通知我們。'
      },
      {
        title: '4. 第三方內容與資料準確性',
        body: '我們的平台依賴多種資料來源（包括第三方資料庫和 AI 模型）來提供餐廳資訊、翻譯和推薦。儘管我們努力確保準確性，但我們無法保證所有細節（如營業時間、菜單和價格）絕對最新或完全準確。我們強烈建議您透過官方管道或直接與餐廳核實關鍵資訊。'
      },
      {
        title: '5. 責任限制',
        body: '在法律允許的最大範圍內，TabeTabi 對因使用或無法使用本服務（包括但不限於依賴從本服務獲得的資訊、錯誤、遺漏、中斷或瑕疵）而導致的任何直接、間接、偶然、特殊或後果性的損害不承擔責任。'
      },
      {
        title: '6. 條款修改',
        body: '我們保留隨時修改本服務條款的權利。我們將始終在網站上發佈最新版本。在變更生效後繼續使用本服務，即表示您同意受修訂後的條款約束。'
      }
    ]
  },
  ja: {
    lastUpdated: '最終更新日：2026年5月15日',
    sections: [
      {
        title: '1. 利用規約の同意',
        body: 'TabeTabi（食べ旅）（以下「本サービス」）にアクセスし利用することにより、お客様は本規約のすべての条件に同意したものとみなされます。また、本サービスを利用する際は、当社の各種ガイドラインやルールにも従うものとします。'
      },
      {
        title: '2. サービスの内容',
        body: 'TabeTabiは、飲食店検索ツール、インタラクティブマップ、AIによる食事の提案、および個人のお気に入り管理（ブックマークやプライベートメモ）を含む豊富なリソースを提供します。本サービスには、当社からのサービスに関するお知らせや管理上の通知が含まれる場合があります。'
      },
      {
        title: '3. ユーザーアカウントと責任',
        body: '一部の機能（お気に入りの保存やメモの作成等）を利用するには、Google等のOAuthプロバイダーまたはメールアドレスを使用してアカウント登録を行う必要があります。アカウント情報の機密保持はお客様の責任となります。アカウントの不正利用またはセキュリティ侵害に気づいた場合は、速やかに当社に通知することに同意するものとします。'
      },
      {
        title: '4. サードパーティのコンテンツとデータの正確性',
        body: '当プラットフォームは、店舗情報、翻訳、おすすめ機能を提供するため、第三者のデータベースやAIモデルなどの様々なデータソースに依存しています。正確性の確保に努めておりますが、営業時間、メニュー、価格などのすべての情報が常に最新かつ正確であることを保証するものではありません。重要な情報は、公式サイトまたは店舗に直接確認することを強く推奨します。'
      },
      {
        title: '5. 免責事項',
        body: 'TabeTabiは、本サービスの利用または利用不能から生じるいかなる直接的、間接的、偶発的、特別、あるいは派生的な損害（本サービスから得られた情報への依存、間違い、情報の欠落、中断、エラー等を含みますがこれらに限定されません）について、一切の責任を負いません。'
      },
      {
        title: '6. 規約の変更',
        body: '当社は、いつでも本規約を変更する権利を留保します。最新のバージョンは常に当サイトに掲載されます。変更の発効後も本サービスの利用を継続することにより、お客様は改定後の規約に拘束されることに同意したものとみなされます。'
      }
    ]
  },
  ko: {
    lastUpdated: '최종 수정일: 2026년 5월 15일',
    sections: [
      {
        title: '1. 약관의 동의',
        body: 'TabeTabi(타베타비)(이하 "서비스")에 접속하고 이용함으로써, 귀하는 본 약관의 모든 조건에 동의하게 됩니다. 또한 본 서비스를 이용할 때 당사가 게시한 각종 가이드라인 및 규칙을 준수해야 합니다.'
      },
      {
        title: '2. 서비스 안내',
        body: 'TabeTabi는 레스토랑 검색 도구, 인터랙티브 지도, AI 기반 식당 추천, 그리고 개인 즐겨찾기 관리(저장 및 개인 메모) 등 다양한 리소스를 사용자에게 제공합니다. 귀하는 서비스 운영에 필요한 공지사항이나 관리자 메시지가 서비스에 포함될 수 있음을 이해하고 동의합니다.'
      },
      {
        title: '3. 사용자 계정 및 책임',
        body: '특정 기능(즐겨찾기 저장 및 메모 작성 등)을 사용하려면 Google과 같은 OAuth 제공업체 또는 이메일을 통해 계정을 등록해야 합니다. 계정 정보의 기밀 유지에 대한 책임은 귀하에게 있습니다. 계정의 무단 사용이나 보안 위반을 인지한 경우 즉시 당사에 통보할 것에 동의합니다.'
      },
      {
        title: '4. 타사 콘텐츠 및 데이터 정확성',
        body: '당사 플랫폼은 레스토랑 정보, 번역 및 추천 기능을 제공하기 위해 타사 데이터베이스와 AI 모델 등 다양한 데이터 소스에 의존합니다. 정보의 정확성을 위해 노력하고 있으나, 영업 시간, 메뉴, 가격 등의 모든 세부 정보가 항상 최신이며 완벽하게 정확하다고 보장할 수는 없습니다. 중요한 정보는 공식 출처나 레스토랑에 직접 확인하는 것을 강력히 권장합니다.'
      },
      {
        title: '5. 책임의 한계',
        body: 'TabeTabi는 서비스에서 얻은 정보에 대한 의존, 오류, 누락, 중단 또는 결함 등을 포함하여 서비스의 사용 또는 사용 불능으로 인해 발생하는 직간접적, 우발적, 특수한 또는 결과적 손해에 대해 법이 허용하는 최대 범위 내에서 책임을 지지 않습니다.'
      },
      {
        title: '6. 약관의 변경',
        body: '당사는 언제든지 본 약관을 수정할 권리가 있습니다. 최신 버전의 약관은 항상 당사 웹사이트에 게시됩니다. 변경 사항이 적용된 후에도 서비스를 계속 이용하는 것은 개정된 약관에 동의하는 것으로 간주됩니다.'
      }
    ]
  }
}

function TermsOfServicePage() {
  const t = useTranslations('footer.nav.legal')
  const locale = useLocale() as keyof typeof content
  
  // Fallback to English if locale is not mapped
  const pageContent = content[locale] || content['en']

  return (
    <div className="container mx-auto max-w-4xl px-4 py-12 md:py-20">
      <h1 className="text-3xl md:text-4xl font-bold mb-8 text-foreground">
        {t('terms')}
      </h1>
      
      <div className="prose prose-stone dark:prose-invert max-w-none">
        <p className="text-lg text-muted-foreground mb-8">
          {pageContent.lastUpdated}
        </p>

        {pageContent.sections.map((section, index) => (
          <section key={index} className="mb-8">
            <h2 className="text-2xl font-semibold mb-4 text-foreground">{section.title}</h2>
            <p className="text-muted-foreground leading-relaxed whitespace-pre-wrap">
              {section.body}
            </p>
          </section>
        ))}
      </div>
    </div>
  )
}
